<template>
  <nav class='main filter-container' :class='{show: showMenu}' ref="containerRef" @mouseenter="hasHoveredMenu = true" @mouseleave="returnToActive">
    <hr class="pill" :style="{ transform: `translateX(${pillPosition}rem)`, width: `${pillWidth}rem`, opacity: pillWidth > 0 ? 0.7 : 0, transition: hasHoveredMenu ? 'transform 0.2s ease-in-out, width 0.2s ease-in-out, opacity 0.2s ease-in-out' : 'none' }" />
    <h5 v-for="page in navigationPages" :key="page?.id">
      <router-link
        @click.native='showMenu = 0'
        :to='pathName(page?.url)'
        v-html="page?.title"
        @mouseenter="setHoveredFilter(pathName(page?.url))"
        class="nav-button"
        active-class="active"
        exact-active-class="active"
      />
    </h5>
  </nav>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { yamlToArray, pageFromPath, pathName } from '@/utils'
import { composables } from '@/composables'
import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
import { useRoute } from 'vue-router'

const showMenu = composables().showMenu
const kql = useStore()
const route = useRoute()

// Pill animation refs
const activeFilter = ref('')
const hoveredFilter = ref('')
const pillPosition = ref(0)
const pillWidth = ref(0)
const hasHoveredMenu = ref(false)
const containerRef = ref(null)

// Resize handler reference for cleanup
let resizeHandler = null

// Get navigation pages from main_navigation site content field
const navigationPages = computed(() => {
  if (!kql.site?.content?.main_navigation || !kql.pages) return []

  // Map UIDs to actual page objects, preserving order
  return yamlToArray(kql.site.content.main_navigation).map(uid => pageFromPath(uid, kql.pages)).filter(page => page) // Remove any null/undefined pages
})

const setHoveredFilter = (value) => {
  hoveredFilter.value = value
  updatePillPosition(value, 'hover')
}

const returnToActive = () => {
  hoveredFilter.value = ''
  updatePillPosition(null, 'active')
}

const updatePillPosition = (hoverPath = null, mode = 'active') => {
  nextTick(() => {
    let button;

    if (mode === 'hover' && hoverPath) {
      // Find button by the path we're hovering
      const buttons = containerRef.value?.querySelectorAll('.nav-button')
      button = Array.from(buttons || []).find(btn => pathName(btn.href) === hoverPath)
    } else {
      // Find the active router-link
      button = containerRef.value?.querySelector('.nav-button.active')
    }

    if (button) {
      // Get the root font size to convert px to rem
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16

      pillPosition.value = (button.offsetLeft - 4) / rootFontSize
      pillWidth.value = button.offsetWidth / rootFontSize
      activeFilter.value = pathName(button.href)
    } else {
      // Keep the pill visible but try to find any button as fallback
      const firstButton = containerRef.value?.querySelector('.nav-button')
      if (firstButton && !activeFilter.value) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16

        pillPosition.value = (firstButton.offsetLeft - 4) / rootFontSize
        pillWidth.value = firstButton.offsetWidth / rootFontSize
        activeFilter.value = pathName(firstButton.href)
      }
    }
  })
}

// Watch route changes to update active pill position
watch(() => route.path, () => {
  // Let router-link-active class get applied first, then update pill
  // Use setTimeout to wait a bit longer for Vue Router to apply classes
  setTimeout(() => {
    updatePillPosition(null, 'active')
  }, 10)
}, { immediate: true })

onMounted(() => {
  // Initial pill positioning based on active router-link
  nextTick(() => {
    // Find and position on the actual active button directly
    setTimeout(() => {
      updatePillPosition(null, 'active')
    }, 50)

    // Also watch for changes to the navigation container
    if (containerRef.value) {
      const observer = new MutationObserver(() => {
        updatePillPosition(null, 'active')
      })

      observer.observe(containerRef.value, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class']
      })
    }

    // Add window resize listener to recalculate pill position and size
    resizeHandler = () => {
      updatePillPosition(null, 'active')
    }

    window.addEventListener('resize', resizeHandler)
  })
})

onUnmounted(() => {
  // Clean up resize listener
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
  }
})
</script>

<style scoped>
@import '@/css/breakpoints.css';

nav.main {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-container {
  position: relative;
  display: inline-flex;
  border-radius: 10rem;
  padding: 0.25rem;
}

hr.pill {
  margin: 0;
  padding: 0;
  display: block;
  position: absolute;
  top: 0.25rem;
  bottom: 0.25rem;
  border-radius: 10rem;
  border-color: var(--dark);
  border-width: 1px;
  border-style: solid;
  z-index: 1;
}

h5 {
  display: inline-block;
  margin: 0;
  user-select: none;
}

:deep(.nav-button) {
  position: relative;
  cursor: pointer;
  background-color: transparent;
  outline: none;
  font-family: inherit;
  margin: 0;
  padding: 0.75rem 1.5rem;
  border: none;
  font-weight: 400;
  text-transform: uppercase;
  border-radius: 10rem;
  z-index: 2;
  transition: transform 0.1s ease-in-out, color 0.3s ease-in-out;
  text-align: center;
  text-decoration: none;
  display: block;
}

:deep(.nav-button:hover) {
  background-color: transparent;
}

:deep(.nav-button:active) {
  color: gray;
}

:deep(.nav-button.active) {
  font-weight: 600;
}

</style>
